import { type DocumentVariableMetadata, documentVariable } from './document-variable'

/**
 * Metadatos de la NEGOCIACIÓN (acuerdo de pago).
 *
 * Las versiones «en letras» de los numéricos son la máscara `letters` y no se duplican acá; los
 * pocos alias `_letras` que existen en plantillas guardadas los emite el orquestador del front.
 */

export const NEGOTIATION_VARIABLE_METADATA: DocumentVariableMetadata[] = [
  documentVariable({
    key: 'upgrades',
    label: '¿Tiene actualizaciones?',
    category: 'negotiation',
    type: 'boolean',
    description: 'SÍ si la negociación tiene órdenes de producción',
    example: 'SI'
  }),
  documentVariable({
    key: 'forma_pago_inmueble',
    label: 'Forma de pago del inmueble',
    category: 'negotiation',
    type: 'enum',
    optionsFromHost: true,
    example: 'Crédito Bancario'
  }),
  documentVariable({
    key: 'terreno_extra',
    label: 'Terreno Extra (mts2)',
    category: 'negotiation',
    type: 'number',
    example: '25'
  }),
  documentVariable({
    key: 'parqueo_sotano',
    label: 'Parqueo y Sótano (legacy)',
    category: 'negotiation',
    type: 'string',
    description: 'Texto libre histórico de parqueo y sótano'
  }),
  documentVariable({
    key: 'valor_total',
    label: 'Valor Total',
    category: 'negotiation',
    type: 'number',
    description: 'Monto total de la negociación',
    example: '850000.00',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'reserva',
    label: 'Reserva',
    category: 'negotiation',
    type: 'number',
    description: 'Monto de reserva de la negociación',
    example: '50000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'total_cuotas',
    label: 'Suma de cuotas (enganche)',
    category: 'negotiation',
    type: 'number',
    description: 'Suma de los montos de las cuotas de enganche',
    example: '150000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'monto_financiar',
    label: 'Monto a financiar',
    category: 'negotiation',
    type: 'number',
    description: 'Valor total − reserva − suma de cuotas',
    example: '650000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'valor_total_con_moneda',
    label: 'Valor Total con Moneda',
    category: 'negotiation',
    type: 'string',
    example: 'Q 850,000.00'
  }),
  documentVariable({
    key: 'fecha_entrega',
    label: 'Fecha de Entrega',
    category: 'negotiation',
    type: 'date',
    example: '15/01/2027'
  }),
  documentVariable({
    key: 'fecha_primera_cuota',
    label: 'Fecha de la primera cuota',
    category: 'negotiation',
    type: 'date',
    description: 'Fecha del primer pago (para cálculos)',
    example: '2026-02-11'
  }),
  documentVariable({
    key: 'fecha_ultima_cuota',
    label: 'Fecha de la última cuota',
    category: 'negotiation',
    type: 'date',
    description: 'Fecha del último pago (vencimiento)',
    example: '2028-07-26'
  }),
  documentVariable({
    key: 'fecha_inicio_plazo',
    label: 'Fecha de inicio del plazo',
    category: 'negotiation',
    type: 'date',
    description: 'Primer pago posterior a (último − 24 meses)',
    example: '2026-08-11'
  }),
  documentVariable({
    key: 'tiene_parqueo',
    label: 'Tiene parqueo',
    category: 'negotiation',
    type: 'boolean',
    description: 'SÍ/NO según el acuerdo de pago',
    example: 'SI'
  }),
  documentVariable({
    key: 'parqueo_numero',
    label: 'Parqueo - Número',
    category: 'negotiation',
    type: 'number',
    example: '168'
  }),
  documentVariable({
    key: 'parqueo_m2_negociacion',
    label: 'Parqueo M2 (negociación)',
    category: 'negotiation',
    type: 'number'
  }),
  documentVariable({
    key: 'parqueo_vehiculos',
    label: 'Parqueo - Vehículos',
    category: 'negotiation',
    type: 'number',
    description: 'Cantidad de vehículos',
    example: '2'
  }),
  documentVariable({
    key: 'parqueo_nivel',
    label: 'Parqueo - Sótano',
    category: 'negotiation',
    type: 'number',
    description: 'Nivel de sótano donde se ubica el parqueo'
  }),
  documentVariable({
    key: 'parqueo_nivel_tipo',
    label: 'Parqueo - Ubicación (Nivel/Sótano)',
    category: 'negotiation',
    type: 'string',
    description: 'Nivel o Sótano, según el tipo elegido para el parqueo'
  }),
  documentVariable({
    key: 'tiene_bodega',
    label: 'Tiene bodega',
    category: 'negotiation',
    type: 'boolean',
    description: 'SÍ/NO según el acuerdo de pago',
    example: 'SI'
  }),
  documentVariable({
    key: 'bodega_numero',
    label: 'Bodega - Número',
    category: 'negotiation',
    type: 'number'
  }),
  documentVariable({
    key: 'bodega_m2',
    label: 'Bodega - M2',
    category: 'negotiation',
    type: 'number',
    description: 'Metros cuadrados de la bodega'
  }),
  documentVariable({
    key: 'bodega_sotano',
    label: 'Bodega - Sótano',
    category: 'negotiation',
    type: 'number',
    description: 'Nivel de sótano donde se ubica la bodega'
  }),
  documentVariable({
    key: 'sotano_nivel_tipo',
    label: 'Bodega - Ubicación (Nivel/Sótano)',
    category: 'negotiation',
    type: 'string',
    description: 'Nivel o Sótano, según el tipo elegido para la bodega'
  }),
  documentVariable({
    key: 'valor_titulo_sin_impuesto',
    label: 'Valor del Título (sin impuesto)',
    category: 'negotiation',
    type: 'number',
    description: 'Valor del título sin impuestos',
    example: '780000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'valor_total_sin_impuesto',
    label: 'Valor Total (sin impuesto)',
    category: 'negotiation',
    type: 'number',
    description: 'Valor total sin impuestos',
    example: '850000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'valor_upgrades_sin_impuesto',
    label: 'Valor de Modificaciones/Upgrades (sin impuesto)',
    category: 'negotiation',
    type: 'number',
    description: 'Valor de modificaciones (upgrades) sin impuestos',
    example: '25000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'valor_inmueble_sin_impuesto',
    label: 'Valor del Inmueble (sin impuesto)',
    category: 'negotiation',
    type: 'number',
    description: 'Valor del inmueble sin impuestos',
    example: '825000',
    masks: ['currency', 'currencyLetters']
  }),
  documentVariable({
    key: 'codigo_negociacion',
    label: 'Código de la negociación',
    category: 'negotiation',
    type: 'string',
    group: 'Datos de la negociación',
    example: '1024'
  }),
  documentVariable({
    key: 'estado_negociacion',
    label: 'Estado actual',
    category: 'negotiation',
    type: 'string',
    group: 'Datos de la negociación',
    description: 'Nombre del estado actual del workflow',
    example: 'En negociación'
  }),
  documentVariable({
    key: 'fecha_inicio_negociacion',
    label: 'Fecha de inicio',
    category: 'negotiation',
    type: 'date',
    group: 'Datos de la negociación',
    example: '2026-01-15'
  }),
  documentVariable({
    key: 'fecha_fin_negociacion',
    label: 'Fecha de cierre',
    category: 'negotiation',
    type: 'date',
    group: 'Datos de la negociación',
    description: 'Fecha de fin de la negociación (si aplica)',
    example: '2026-03-20'
  }),
  documentVariable({
    key: 'tiene_sotano',
    label: 'Tiene bodega (legacy)',
    category: 'negotiation',
    type: 'boolean',
    description: 'Usar tiene_bodega en plantillas nuevas',
    example: 'SI'
  }),
  documentVariable({
    key: 'sotano_numero',
    label: 'Sótano - Número (legacy)',
    category: 'negotiation',
    type: 'number',
    description: 'Campo eliminado del formulario; solo disponible en negociaciones históricas'
  }),
  documentVariable({
    key: 'sotano_m2',
    label: 'Bodega M2 (legacy)',
    category: 'negotiation',
    type: 'number',
    description: 'Usar bodega_m2 en plantillas nuevas'
  }),
  documentVariable({
    key: 'sotano_nivel',
    label: 'Bodega - Sótano (legacy)',
    category: 'negotiation',
    type: 'number',
    description: 'Usar bodega_sotano en plantillas nuevas'
  })
]
